import {
  MapPin,
  Users,
  Star,
  Calendar,
  Clock,
  Wifi,
  Car,
  Coffee,
  Shield,
  Zap,
  Volume2,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";

import EventCard from "../../../components/event/event-card/EventCard";
import SeatMap from "../../../components/ground/seat-map/SeatMap";
import GroundMap from "../../../components/maps/ground-map/GroundMap";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useGetEventsByGroundQuery } from "../../../store/api/eventApi";
import { useGetGroundByIdQuery, useGetGroundAvailabilityQuery } from "../../../store/api/groundApi";
import { formatCurrency, capitalizeFirst } from "../../../utils/helpers";

const GroundDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0] || ""
  );

  const {
    data: groundData,
    isLoading: groundLoading,
    error: groundError,
  } = useGetGroundByIdQuery(id!);
  const { data: availabilityData } = useGetGroundAvailabilityQuery({ id: id!, date: selectedDate });
  const { data: eventsData } = useGetEventsByGroundQuery(id!);

  const ground = groundData?.data;
  const events = eventsData?.data || [];

  const getAmenityIcon = (amenity: string) => {
    switch (amenity.toLowerCase()) {
      case "wifi":
        return <Wifi className='h-5 w-5' />;
      case "parking":
        return <Car className='h-5 w-5' />;
      case "cafe":
      case "cafeteria":
        return <Coffee className='h-5 w-5' />;
      case "security":
        return <Shield className='h-5 w-5' />;
      case "lighting":
        return <Zap className='h-5 w-5' />;
      case "sound system":
        return <Volume2 className='h-5 w-5' />;
      default:
        return <div className='h-5 w-5 rounded-full bg-primary/20' />;
    }
  };

  if (groundLoading) {
    return (
      <div className='container py-8'>
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          <div className='space-y-6 lg:col-span-2'>
            <Skeleton className='aspect-video' />
            <div className='space-y-4'>
              <Skeleton className='h-8 w-3/4' />
              <Skeleton className='h-4 w-1/2' />
              <Skeleton className='h-20' />
            </div>
          </div>
          <div className='space-y-6'>
            <Card>
              <CardContent className='p-6'>
                <Skeleton className='h-32' />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (groundError || !ground) {
    return (
      <div className='container py-8'>
        <div className='text-center'>
          <h1 className='mb-4 text-2xl font-bold'>Ground Not Found</h1>
          <p className='mb-6 text-muted-foreground'>
            The ground you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button asChild>
            <Link to='/grounds'>Browse All Grounds</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
        {/* Main Content */}
        <div className='space-y-6 lg:col-span-2'>
          {/* Hero Image */}
          <div className='aspect-video rounded-lg bg-gradient-to-r from-green-500 to-blue-500' />

          {/* Ground Info */}
          <div>
            <div className='mb-4 flex items-start justify-between'>
              <div>
                <div className='mb-2 flex items-center gap-3'>
                  <h1 className='text-3xl font-bold'>{ground.name}</h1>
                  <Badge variant='secondary' className='capitalize'>
                    {ground.type}
                  </Badge>
                </div>
                <div className='mb-2 flex items-center text-muted-foreground'>
                  <MapPin className='mr-2 h-4 w-4' />
                  {ground.location.address}, {ground.location.city}, {ground.location.country}
                </div>
                <div className='flex items-center gap-4 text-sm'>
                  <div className='flex items-center'>
                    <Users className='mr-1 h-4 w-4' />
                    {ground.capacity.toLocaleString()} capacity
                  </div>
                  <div className='flex items-center'>
                    <Star className='mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' />
                    4.5 (120 reviews)
                  </div>
                </div>
              </div>
              <div className='text-right'>
                <div className='text-2xl font-bold'>
                  {formatCurrency(ground.pricing.basePrice, ground.pricing.currency)}
                </div>
                <div className='text-sm text-muted-foreground'>per hour</div>
              </div>
            </div>

            {/* Amenities */}
            <div className='mb-6 grid grid-cols-2 gap-4 md:grid-cols-4'>
              {ground.amenities.map((amenity, index) => (
                <div key={index} className='flex items-center gap-2 rounded-lg bg-muted/50 p-3'>
                  {getAmenityIcon(amenity)}
                  <span className='text-sm'>{capitalizeFirst(amenity)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue='overview' className='w-full'>
            <TabsList className='grid w-full grid-cols-4'>
              <TabsTrigger value='overview'>Overview</TabsTrigger>
              <TabsTrigger value='seats'>Seat Map</TabsTrigger>
              <TabsTrigger value='events'>Events</TabsTrigger>
              <TabsTrigger value='location'>Location</TabsTrigger>
            </TabsList>

            <TabsContent value='overview' className='space-y-6'>
              <Card>
                <CardHeader>
                  <CardTitle>About This Ground</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-muted-foreground'>
                    This is a premium {ground.type} ground located in {ground.location.city}. With a
                    capacity of {ground.capacity.toLocaleString()} people, it&apos;s perfect for
                    both small and large events. The ground features modern amenities and excellent
                    facilities to ensure a great experience for all visitors.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Ground Rules & Policies</CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                  <div className='flex items-center gap-2'>
                    <Clock className='h-4 w-4' />
                    <span className='text-sm'>Operating hours: 6:00 AM - 10:00 PM</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Calendar className='h-4 w-4' />
                    <span className='text-sm'>Advance booking required (minimum 24 hours)</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Shield className='h-4 w-4' />
                    <span className='text-sm'>
                      Security deposit may be required for large events
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value='seats'>
              <Card>
                <CardHeader>
                  <CardTitle>Seating Layout</CardTitle>
                </CardHeader>
                <CardContent>
                  <SeatMap ground={ground} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value='events'>
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Upcoming Events</h3>
                {events.length > 0 ? (
                  <div className='grid gap-4'>
                    {events.map(event => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className='p-6 text-center'>
                      <p className='text-muted-foreground'>No upcoming events at this ground.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value='location'>
              <Card>
                <CardHeader>
                  <CardTitle>Location & Directions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-4'>
                    <div>
                      <h4 className='mb-2 font-medium'>Address</h4>
                      <p className='text-muted-foreground'>
                        {ground.location.address}
                        <br />
                        {ground.location.city}, {ground.location.country}
                      </p>
                    </div>
                    <GroundMap
                      coordinates={ground.location.coordinates}
                      name={ground.name}
                      address={ground.location.address}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className='space-y-6'>
          {/* Booking Card */}
          <Card>
            <CardHeader>
              <CardTitle>Book This Ground</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div>
                <label className='mb-2 block text-sm font-medium'>Select Date</label>
                <input
                  type='date'
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className='w-full rounded-md border p-2'
                />
              </div>

              {availabilityData?.data && (
                <div className='rounded-lg bg-muted/50 p-3'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm'>Availability</span>
                    <Badge variant={availabilityData.data.available ? "default" : "destructive"}>
                      {availabilityData.data.available ? "Available" : "Booked"}
                    </Badge>
                  </div>
                </div>
              )}

              <Button className='w-full' size='lg'>
                Check Events & Book
              </Button>

              <div className='text-center'>
                <Button variant='outline' className='w-full bg-transparent'>
                  Contact Ground Manager
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Ground Statistics</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex justify-between'>
                <span className='text-sm text-muted-foreground'>Total Events</span>
                <span className='font-medium'>156</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-muted-foreground'>Average Rating</span>
                <span className='font-medium'>4.5/5</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-muted-foreground'>Response Time</span>
                <span className='font-medium'>&lt; 2 hours</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-muted-foreground'>Booking Rate</span>
                <span className='font-medium'>89%</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GroundDetails;
